import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Box, Chip, Alert, Tabs, Tab, FormControlLabel, Checkbox, Tooltip, IconButton
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import InfoIcon from '@mui/icons-material/Info';
import billingApi from '../../services/billingApi';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import useConfirm from '../../hooks/useConfirm';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

const EMPTY_FORM = {
  code: '', description: '', discount_type: 'percentage', discount_value: '',
  duration: 'once', duration_months: '', max_redemptions: '', max_per_user: '1',
  valid_from: '', valid_until: '',
  display_label: '', min_subscription_days: '', eligible_change_type: 'any',
  auto_apply: false, first_purchase_only: false,
  restricted_emails: '', restricted_phones: ''
};

export default function DiscountManagement() {
  const [discounts, setDiscounts] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(false);
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const { confirm, confirmState, closeConfirm } = useConfirm();

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const [discData, refData] = await Promise.all([
        billingApi.get('/api/admin/discounts'),
        billingApi.get('/api/admin/referrals').catch(() => ({ codes: [] }))
      ]);
      setDiscounts(discData.discounts || []);
      setReferrals(refData.codes || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    try {
      const body = {
        ...form,
        discount_value: parseInt(form.discount_value),
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
        max_per_user: parseInt(form.max_per_user) || 1,
        duration_months: form.duration === 'repeating' && form.duration_months ? parseInt(form.duration_months) : null,
        min_subscription_days: form.min_subscription_days ? parseInt(form.min_subscription_days) : 0,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        restricted_emails: form.restricted_emails ? form.restricted_emails.split(',').map(e => e.trim()).filter(Boolean) : null,
        restricted_phones: form.restricted_phones ? form.restricted_phones.split(',').map(p => p.trim()).filter(Boolean) : null
      };
      await billingApi.post('/api/admin/discounts', body);
      setDialog(false);
      setForm({ ...EMPTY_FORM });
      setError('');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDeactivate(id) {
    confirm({
      title: 'Deactivate Discount',
      message: 'Deactivate this discount code?',
      destructive: true,
      confirmLabel: 'Deactivate',
      onConfirm: async () => {
        try {
          await billingApi.del(`/api/admin/discounts/${id}`);
          loadData();
        } catch (err) {
          setError(err.message);
        }
      },
    });
  }

  function formatValue(d) {
    if (d.discount_type === 'percentage') return `${d.discount_value}%`;
    if (d.discount_type === 'fixed_amount') return `$${(d.discount_value / 100).toFixed(2)}`;
    if (d.discount_type === 'trial_extension') return `${d.discount_value} days`;
    if (d.discount_type === 'free_period') return `${d.discount_value} months`;
    return d.discount_value;
  }

  function formatDuration(d) {
    if (d.duration === 'once') return 'Once';
    if (d.duration === 'repeating') return `${d.duration_months || '?'} months`;
    if (d.duration === 'forever') return 'Forever';
    return d.duration;
  }

  function formatRestrictions(d) {
    const tags = [];
    if (d.first_purchase_only) tags.push('First purchase');
    if (d.min_subscription_days > 0) tags.push(`${d.min_subscription_days}d loyalty`);
    if (d.eligible_change_type && d.eligible_change_type !== 'any') tags.push(d.eligible_change_type + ' only');
    if (d.auto_apply) tags.push('Auto');
    const emails = d.restricted_emails ? (typeof d.restricted_emails === 'string' ? JSON.parse(d.restricted_emails) : d.restricted_emails) : null;
    if (emails && emails.length) tags.push(`${emails.length} email(s)`);
    return tags;
  }

  const discountColumns = useMemo(() => [
    {
      field: 'code',
      headerName: 'Code',
      width: 150,
      renderCell: (row) => (
        <Box>
          <code style={{ fontWeight: 600 }}>{row.code}</code>
          {row.display_label && <Typography variant="caption" display="block" color="text.secondary">{row.display_label}</Typography>}
        </Box>
      ),
    },
    {
      field: 'discount_type',
      headerName: 'Type',
      width: 130,
      type: 'singleSelect',
      filterOptions: ['percentage', 'fixed_amount', 'trial_extension', 'free_period'],
      renderCell: (row) => row.discount_type?.replace(/_/g, ' ') || '',
    },
    {
      field: 'discount_value',
      headerName: 'Value',
      width: 100,
      renderCell: (row) => formatValue(row),
      sortable: false,
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 120,
      type: 'singleSelect',
      filterOptions: ['once', 'repeating', 'forever'],
      renderCell: (row) => formatDuration(row),
    },
    {
      field: 'current_redemptions',
      headerName: 'Redemptions',
      width: 130,
      valueGetter: (params) => `${params.row.current_redemptions}/${params.row.max_redemptions || '\u221E'}`,
    },
    {
      field: 'restrictions',
      headerName: 'Restrictions',
      width: 200,
      filterable: false,
      sortable: false,
      renderCell: (row) => {
        const tags = formatRestrictions(row);
        return tags.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {tags.map(tag => <Chip key={tag} label={tag} size="small" variant="outlined" />)}
          </Box>
        ) : <Typography variant="caption" color="text.secondary">None</Typography>;
      },
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 110,
      type: 'singleSelect',
      filterOptions: ['Active', 'Inactive'],
      valueGetter: (params) => params.row.is_active ? 'Active' : 'Inactive',
      renderCell: (row) => {
        const label = row.is_active ? 'Active' : 'Inactive';
        return <Chip label={label} color={label === 'Active' ? 'success' : 'default'} size="small" />;
      },
    },
    {
      field: 'valid_until',
      headerName: 'Expires',
      width: 130,
      type: 'date',
      renderCell: (row) => row.valid_until ? new Date(row.valid_until).toLocaleDateString() : '\u2014',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (row) => row.is_active ? (
        <Button size="small" color="error" onClick={() => handleDeactivate(row.id)}>Deactivate</Button>
      ) : null,
    },
  ], []);

  const referralColumns = useMemo(() => [
    {
      field: 'code',
      headerName: 'Code',
      width: 150,
      renderCell: (row) => <code style={{ fontWeight: 600 }}>{row.code}</code>,
    },
    { field: 'customer_email', headerName: 'Owner', width: 180 },
    { field: 'total_referrals', headerName: 'Referrals', width: 100, type: 'numeric' },
    { field: 'successful_referrals', headerName: 'Successful', width: 100, type: 'numeric' },
    {
      field: 'max_referrals',
      headerName: 'Max',
      width: 80,
      renderCell: (row) => row.max_referrals || '\u221E',
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 110,
      type: 'singleSelect',
      filterOptions: ['Active', 'Inactive'],
      valueGetter: (params) => params.row.is_active ? 'Active' : 'Inactive',
      renderCell: (row) => {
        const label = row.is_active ? 'Active' : 'Inactive';
        return <Chip label={label} color={label === 'Active' ? 'success' : 'default'} size="small" />;
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 140,
      type: 'date',
      renderCell: (row) => new Date(row.created_at).toLocaleDateString(),
    },
  ], []);

  if (loading) return <AdminPageSkeleton />;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Discounts & Referrals</Typography>
        <Button variant="contained" onClick={() => setDialog(true)}>Create Discount</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Discount Codes (${discounts.length})`} />
        <Tab label={`Referral Codes (${referrals.length})`} />
      </Tabs>

      {tab === 0 && (
        <Paper variant="outlined" sx={{ height: 600 }}>
          <AdminTable
            rows={discounts}
            columns={discountColumns}
            defaultSort={[]}
          />
        </Paper>
      )}

      {tab === 1 && (
        <Paper variant="outlined" sx={{ height: 600 }}>
          <AdminTable
            rows={referrals}
            columns={referralColumns}
            defaultSort={[]}
          />
        </Paper>
      )}

      {/* Create Discount Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Discount Code</DialogTitle>
        <DialogContent>
          {/* Basic Info */}
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Basic</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField label="Code" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} sx={{ flex: 1 }} />
            <TextField label="Display Label" value={form.display_label} onChange={e => setForm({ ...form, display_label: e.target.value })} sx={{ flex: 1 }} helperText="Shown to users (e.g., 'New Member Offer')" />
          </Box>
          <TextField fullWidth label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} sx={{ mb: 2 }} />

          {/* Discount Details */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Discount</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Type</InputLabel>
              <Select value={form.discount_type} label="Type" onChange={e => setForm({ ...form, discount_type: e.target.value })}>
                <MenuItem value="percentage">Percentage (%)</MenuItem>
                <MenuItem value="fixed_amount">Fixed Amount (cents)</MenuItem>
                <MenuItem value="trial_extension">Trial Extension (days)</MenuItem>
                <MenuItem value="free_period">Free Period (months)</MenuItem>
              </Select>
            </FormControl>
            <TextField type="number" label="Value" value={form.discount_value} onChange={e => setForm({ ...form, discount_value: e.target.value })} sx={{ flex: 1 }}
              helperText={form.discount_type === 'percentage' ? '1-100' : form.discount_type === 'fixed_amount' ? 'Amount in cents' : form.discount_type === 'trial_extension' ? 'Days' : 'Months'} />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Duration</InputLabel>
              <Select value={form.duration} label="Duration" onChange={e => setForm({ ...form, duration: e.target.value })}>
                <MenuItem value="once">Once (first invoice only)</MenuItem>
                <MenuItem value="repeating">Repeating (N months)</MenuItem>
                <MenuItem value="forever">Forever (all invoices)</MenuItem>
              </Select>
            </FormControl>
            {form.duration === 'repeating' && (
              <TextField type="number" label="Duration Months" value={form.duration_months} onChange={e => setForm({ ...form, duration_months: e.target.value })} sx={{ flex: 1 }} />
            )}
          </Box>

          {/* Limits */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Limits</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField type="number" label="Max Redemptions" value={form.max_redemptions} onChange={e => setForm({ ...form, max_redemptions: e.target.value })} helperText="Empty = unlimited" sx={{ flex: 1 }} />
            <TextField type="number" label="Max Per User" value={form.max_per_user} onChange={e => setForm({ ...form, max_per_user: e.target.value })} sx={{ flex: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField type="date" label="Valid From" InputLabelProps={{ shrink: true }} value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })} sx={{ flex: 1 }} />
            <TextField type="date" label="Valid Until" InputLabelProps={{ shrink: true }} value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} sx={{ flex: 1 }} />
          </Box>

          {/* Restrictions */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Restrictions</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Eligible For</InputLabel>
              <Select value={form.eligible_change_type} label="Eligible For" onChange={e => setForm({ ...form, eligible_change_type: e.target.value })}>
                <MenuItem value="any">Any subscription</MenuItem>
                <MenuItem value="new">New subscriptions only</MenuItem>
                <MenuItem value="upgrade">Upgrades only</MenuItem>
                <MenuItem value="renewal">Renewals only</MenuItem>
              </Select>
            </FormControl>
            <TextField type="number" label="Min Subscription Days" value={form.min_subscription_days} onChange={e => setForm({ ...form, min_subscription_days: e.target.value })} sx={{ flex: 1 }} helperText="Loyalty requirement (0 = none)" />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <FormControlLabel control={<Checkbox checked={form.first_purchase_only} onChange={e => setForm({ ...form, first_purchase_only: e.target.checked })} />} label="First purchase only" />
            <FormControlLabel control={<Checkbox checked={form.auto_apply} onChange={e => setForm({ ...form, auto_apply: e.target.checked })} />}
              label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Auto-apply <Tooltip title="Automatically show this discount to eligible users without code entry"><IconButton size="small" aria-label="Auto-apply info"><InfoIcon fontSize="small" /></IconButton></Tooltip></Box>} />
          </Box>
          <TextField fullWidth label="Restricted Emails" value={form.restricted_emails} onChange={e => setForm({ ...form, restricted_emails: e.target.value })} sx={{ mb: 2 }} helperText="Comma-separated. Leave empty for all users." />
          <TextField fullWidth label="Restricted Phones" value={form.restricted_phones} onChange={e => setForm({ ...form, restricted_phones: e.target.value })} helperText="Comma-separated. Leave empty for all users." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.code || !form.discount_value}>Create</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...confirmState} onClose={closeConfirm} />
    </Container>
  );
}
